"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useLocale } from "next-intl";
import { useTransition } from "react";

import FlagArabic from "@/components/Icons/flags/FlagArabic";
import FlagEnglish from "@/components/Icons/flags/FlagEnglish";
import IconArrowDown from "@/components/Icons/IconArrowDown";
import { SITE_COOKIES, useCookie } from "@/hooks/useCookies";
import { usePathname } from "@/i18n/routing";
import { getCookieDomain } from "@/lib/utils";

const LanguageSwitcher = () => {
  const locale = useLocale();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  const [_cookieLocale, setCookieLocale] = useCookie(SITE_COOKIES.locale, {
    defaultValue: "en",
    maxAgeSeconds: "1 year",
    domain: getCookieDomain(),
  });

  const handleLocaleChange = (newLocale: "en" | "ar") => {
    if (newLocale === locale) return;

    setCookieLocale(newLocale);

    startTransition(() => {
      // router.replace(pathname, { locale: newLocale });
      const newPath = `/${newLocale}${pathname}`;
      window.location.href = newPath;
    });
  };

  const isEnglish = locale === "en";
  const isArabic = locale === "ar";

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <div className="me-2 flex h-14 cursor-pointer rounded-20 border border-blue px-3 transition-all duration-300 hover:bg-secondary-light 2xl:me-10 ultrawide:me-2">
          <button
            className="flex items-center gap-2"
            aria-label="Select language"
            aria-controls="language-menu"
            aria-haspopup="listbox"
            aria-expanded="false"
            disabled={isPending}
          >
            <IconArrowDown width="16" fill="#000" aria-hidden="true" />
            {/* {isEnglish ? <FlagEnglish aria-hidden="true" /> : <FlagArabic aria-hidden="true" />} */}
            {isEnglish ? (
              <span className="urbanist flex items-center gap-1 text-sm font-semibold">EN</span>
            ) : (
              <span className="urbanist flex items-center gap-1 text-sm font-semibold">AR</span>
            )}
            <span className="sr-only">Current language: {isEnglish ? "English" : "العربية"}</span>
          </button>
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[8000] ml-auto min-w-[200px] rounded-2xl bg-white shadow-lg data-[side=bottom]:animate-slideDown data-[side=top]:animate-slideUp"
          sideOffset={8}
          avoidCollisions
          role="listbox"
          aria-label="Select language"
          aria-orientation="vertical"
          aria-multiselectable="false"
        >
          <DropdownMenu.Item
            className="flex items-center justify-between rounded-2xl border-none p-4 outline-none transition-all duration-300 hover:cursor-pointer hover:bg-secondary-light focus:bg-blue-light focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            role="option"
            aria-selected={isEnglish}
            tabIndex={0}
            onClick={() => handleLocaleChange("en")}
            disabled={isPending}
          >
            <span className="inter font-semibold text-black">English - EN</span>
            <FlagEnglish aria-hidden="true" />
          </DropdownMenu.Item>
          <hr className="mx-4" aria-orientation="horizontal" />
          <DropdownMenu.Item
            className="flex items-center justify-between rounded-2xl border-none p-4 outline-none transition-all duration-300 hover:cursor-pointer hover:bg-secondary-light focus:bg-blue-light focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            role="option"
            aria-selected={isArabic}
            dir="rtl"
            tabIndex={0}
            onClick={() => handleLocaleChange("ar")}
            disabled={isPending}
          >
            <FlagArabic aria-hidden="true" />
            <span className="inter font-semibold text-black">العربية - AR</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default LanguageSwitcher;
